import PageHeader from '../../components/PageHeader';
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Alert, IconButton, Tooltip,
  useTheme, useMediaQuery, Stack, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { User } from '../../types';
import RoleGuard from '../../components/RoleGuard';

import { formatDate } from '../../utils/dateUtils';

interface CreateUserForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
}

const ROLE_LABELS: Record<string, string> = {
  company_admin: 'Company Admin',
  fleet_manager: 'Fleet Manager',
  alert_manager: 'Alert Manager',
  maintenance: 'Maintenance',
  viewer: 'Viewer',
};

const ROLE_COLORS: Record<string, 'error' | 'primary' | 'warning' | 'info' | 'secondary'> = {
  company_admin: 'error',
  fleet_manager: 'primary',
  alert_manager: 'warning',
  maintenance: 'info',
  viewer: 'secondary',
};

const ASSIGNABLE_ROLES = ['fleet_manager', 'alert_manager', 'maintenance', 'viewer'];

const UserManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { company, user: currentUser } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    firstName: '', lastName: '', email: '', password: '', role: 'viewer',
  });
  const [creating, setCreating] = useState(false);

  // Edit role dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editRole, setEditRole] = useState('');

  // Deactivate dialog
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [targetUser, setTargetUser] = useState<User | null>(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auth/company-users');
      setUsers(res.data.data || []);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // ── Create user ─────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    try {
      setError('');
      setCreating(true);
      await api.post('/auth/create-user', createForm);
      setSuccess('User created successfully');
      setCreateOpen(false);
      setCreateForm({ firstName: '', lastName: '', email: '', password: '', role: 'viewer' });
      await loadUsers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  // ── Edit role ────────────────────────────────────────────────────────────────
  const openEdit = (user: User) => {
    setEditUser(user);
    setEditRole(user.role);
    setEditOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!editUser) return;
    try {
      setError('');
      await api.put(`/auth/users/${editUser._id}/role`, { role: editRole });
      setSuccess('Role updated successfully');
      setEditOpen(false);
      await loadUsers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update role');
    }
  };

  // ── Deactivate ───────────────────────────────────────────────────────────────
  const openDeactivate = (user: User) => {
    setTargetUser(user);
    setDeactivateOpen(true);
  };

  const handleDeactivate = async () => {
    if (!targetUser) return;
    try {
      setError('');
      await api.put(`/auth/users/${targetUser._id}/deactivate`);
      setSuccess('User deactivated');
      setDeactivateOpen(false);
      await loadUsers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to deactivate user');
    }
  };

  if (loading) return <Typography sx={{ p: 3 }}>Loading users...</Typography>;

  return (
    <RoleGuard roles={['company_admin', 'fleet_manager']}>
      <Box>
        {/* Header */}
        <PageHeader title="Users" description="Manage your team, access roles, and workspace permissions" icon="people" actions={<>
<RoleGuard roles="company_admin">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Add User
            </Button>
          </RoleGuard>
</>} />

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Card>
          <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" gutterBottom>
              {company?.name} — Team Members ({users.length})
            </Typography>

            {isMobile ? (
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {users.map((u) => (
                  <Card key={u._id} variant="outlined">
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{u.firstName} {u.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {u.role !== 'company_admin' && (
                            <IconButton size="small" onClick={() => openEdit(u)}><EditIcon fontSize="small" /></IconButton>
                          )}
                          {u.role !== 'company_admin' && u._id !== currentUser?._id && (
                            <IconButton size="small" color="error" onClick={() => openDeactivate(u)}><DeleteIcon fontSize="small" /></IconButton>
                          )}
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Role:</Typography>
                          <Chip label={ROLE_LABELS[u.role] || u.role} color={ROLE_COLORS[u.role] || 'default'} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Status:</Typography>
                          <Chip label={u.isActive ? 'Active' : 'Inactive'} color={u.isActive ? 'success' : 'default'} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Last Login:</Typography>
                          <Typography variant="caption">{u.lastLogin ? formatDate(u.lastLogin) : 'Never'}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u._id} hover>
                        <TableCell>{u.firstName} {u.lastName}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Chip label={ROLE_LABELS[u.role] || u.role} color={ROLE_COLORS[u.role] || 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={u.isActive ? 'Active' : 'Inactive'} color={u.isActive ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}</TableCell>
                        <TableCell align="right">
                          {u.role !== 'company_admin' && (
                            <Tooltip title="Edit Role">
                              <IconButton size="small" onClick={() => openEdit(u)}><EditIcon /></IconButton>
                            </Tooltip>
                          )}
                          {u.role !== 'company_admin' && u._id !== currentUser?._id && (
                            <Tooltip title="Deactivate">
                              <IconButton size="small" color="error" onClick={() => openDeactivate(u)}><DeleteIcon /></IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* ── Create User Dialog ──────────────────────────────────────────────── */}
        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullScreen={isMobile} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Add New User</Typography>
              <IconButton onClick={() => setCreateOpen(false)} size="small"><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth label="First Name" size="small"
                  value={createForm.firstName}
                  onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                />
                <TextField
                  fullWidth label="Last Name" size="small"
                  value={createForm.lastName}
                  onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                />
              </Stack>
              <TextField
                fullWidth label="Email" type="email" size="small"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              />
              <TextField
                fullWidth label="Password" type="password" size="small"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                helperText="Minimum 8 characters"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  label="Role"
                >
                  {ASSIGNABLE_ROLES.map((r) => (
                    <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setCreateOpen(false)} variant="outlined" fullWidth={isMobile}>Cancel</Button>
            <Button onClick={handleCreate} variant="contained" disabled={creating} fullWidth={isMobile}>
              {creating ? 'Creating...' : 'Create User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Edit Role Dialog ────────────────────────────────────────────────── */}
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Edit Role — {editUser?.firstName} {editUser?.lastName}</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 1 }} size="small">
              <InputLabel>Role</InputLabel>
              <Select value={editRole} onChange={(e) => setEditRole(e.target.value)} label="Role">
                {ASSIGNABLE_ROLES.map((r) => (
                  <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateRole} variant="contained">Update</Button>
          </DialogActions>
        </Dialog>

        {/* ── Deactivate Dialog ───────────────────────────────────────────────── */}
        <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Deactivate User</DialogTitle>
          <DialogContent>
            <Typography>
              Deactivate <strong>{targetUser?.firstName} {targetUser?.lastName}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              They won't be able to log in. Their data is preserved.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeactivateOpen(false)}>Cancel</Button>
            <Button onClick={handleDeactivate} variant="contained" color="error">Deactivate</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </RoleGuard>
  );
};

export default UserManagementPage;